import { GTMEvents } from '../../../polly-manage-analytics.constants';
import { BatchFiltersOptions, SelectedFilters, SortFilterOptions, EdgeStructure, NodeStructure, FilterOptions, BatchesFilterEvents, RunsFilterEvents } from '../@core/interfaces';
import { DrawerItemConfig, TableColumnSchema, PollyStatusData, AllStatus, Priority, StatusWidgetLabelColor } from 'polly-qubit';

export const DEFAULT_BATCHES_PAGE_SIZE = 50;
export const DEFAULT_RUNS_PAGE_SIZE = 25;
export const ONE_MINUTE = 60000;
export const TEN_MINUTES = 600000;

export const NO_DAG_MESSAGE = 'DAG is not available for this run.';

export enum BatchColumnType {
  STATUS = 'status',
  USER_NAME = 'user_name',
  PRIORITY = 'priority',
  CREATED_AT = 'created_at',
}

export const SORT_OPTIONS: SortFilterOptions = {
  status: {
    asc: 'Sort A to Z',
    desc: 'Sort Z to A',
  },
  user_name: {
    asc: 'Sort A to Z',
    desc: 'Sort Z to A',
  },
  created_at: {
    asc: 'Sort Old to New',
    desc: 'Sort New to Old',
  },
  priority: {
    asc: 'Sort low to high',
    desc: 'Sort high to low',
  },
};

export const BATCH_PRIORITY_OPTIONS: BatchFiltersOptions[] = [
  { name: 'High', value: 'high' },
  { name: 'Medium', value: 'medium' },
  { name: 'Low', value: 'low' },
];

export const STATUS_NAMES: { [key: string]: AllStatus } = {
  IN_PROGRESS: 'RUNNING',
  PENDING: 'PENDING',
  ERROR: 'ERROR',
  COMPLETED: 'COMPLETED',
  PARTIALLY_COMPLETED: 'PARTIALLY COMPLETED',
  FINISHED: 'FINISHED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  RUNNING: 'RUNNING',
  WAITING: 'WAITING',
  CREATED: 'CREATED',
  SUBMITTED: 'SUBMITTED',
  STARTED: 'STARTED',
  CACHED: 'CACHED',
};

export const BATCH_STATUS_OPTIONS: BatchFiltersOptions[] = [
  { name: 'All', value: 'all' },
  { name: 'Running', value: 'IN_PROGRESS' },
  { name: 'Pending', value: 'PENDING' },
  { name: 'Errored', value: 'ERRORED' },
];

export const STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  PENDING: 'PENDING',
  ERRORED: 'ERRORED',
  PARTIALLY_COMPLETED: 'PARTIALLY_COMPLETED',
  COMPLETED: 'COMPLETED',
  FINISHED: 'FINISHED',
  REJECTED: 'REJECTED',
  ERROR: 'ERROR',
  PRE_PROCESSING: 'PRE-PROCESSING',
  CANCELLED: 'CANCELLED',
  RUNNING: 'RUNNING',
  WAITING: 'WAITING',
  CREATED: 'CREATED',
  SUBMITTED: 'SUBMITTED',
  STARTED: 'STARTED',
  CACHED: 'CACHED',
};

export const STATUS_COLORS: { [key: string]: StatusWidgetLabelColor } = {
  RED: 'red',
  BLUE: 'blue',
  GREEN: 'green',
  YELLOW: 'yellow',
  NEUTRAL_ORANGE: 'neutral-orange',
  PRIMARY_NEUTRAL: 'primary-neutral',
  SECONDARY_BLUE: 'secondary-blue',
  CYAN: 'cyan',
  NEUTRAL_RED: 'neutral-red',
  ORANGE: 'orange',
  PURPLE: 'purple',
};

export const BATCH_STATUS_OPTIONS_ALL: PollyStatusData[] = [
  { label: STATUS_NAMES.ERROR, color: STATUS_COLORS.RED, value: STATUS.ERROR },
  { label: STATUS_NAMES.IN_PROGRESS, color: STATUS_COLORS.BLUE, value: STATUS.IN_PROGRESS },
  { label: STATUS_NAMES.PENDING, color: STATUS_COLORS.YELLOW, value: STATUS.PENDING },
  { label: STATUS_NAMES.COMPLETED, color: STATUS_COLORS.GREEN, value: STATUS.COMPLETED },
];

export const RUNS_STATUS_OPTIONS_ALL: PollyStatusData[] = [
  { label: STATUS_NAMES.ERROR, color: STATUS_COLORS.RED, value: STATUS.ERROR },
  { label: STATUS_NAMES.RUNNING, color: STATUS_COLORS.BLUE, value: STATUS.RUNNING },
  { label: STATUS_NAMES.PENDING, color: STATUS_COLORS.YELLOW, value: STATUS.PENDING },
  { label: STATUS_NAMES.COMPLETED, color: STATUS_COLORS.GREEN, value: STATUS.COMPLETED },
  { label: STATUS_NAMES.CANCELLED, color: STATUS_COLORS.NEUTRAL_ORANGE, value: STATUS.CANCELLED },
];

export const BATCHES_RUNS_EVENT: { batches: BatchesFilterEvents; runs: RunsFilterEvents } = {
  batches: {
    status: GTMEvents.BATCH_STATUS_FILTER,
    status_clear: GTMEvents.BATCH_STATUS_FILTER_CLEAR,
    user_name: GTMEvents.BATCH_STARTED_BY_FILTER,
    user_name_clear: GTMEvents.BATCH_STARTED_BY_FILTER_CLEAR,
    created_at: GTMEvents.BATCH_STARTED_ON_VALUE,
    created_at_clear: GTMEvents.BATCH_STARTED_ON_VALUE_CLEAR,
    created_at_sort: GTMEvents.BATCH_STARTED_ON_SORT,
    priority: GTMEvents.BATCH_PRIORITY_FILTER_VALUE,
    priority_clear: GTMEvents.BATCH_PRIORITY_FILTER_VALUE_CLEAR,
    priority_sort: GTMEvents.BATCH_PRIORITY_FILTER_SORT,
  },
  runs: {
    status: GTMEvents.RUN_STATUS_FILTER,
    status_clear: GTMEvents.RUN_STATUS_FILTER_CLEAR,
    created_at: GTMEvents.RUN_STARTED_ON_VALUE,
    created_at_clear: GTMEvents.RUN_STARTED_ON_VALUE_CLEAR,
    created_at_sort: GTMEvents.RUN_STARTED_ON_SORT,
  },
};

export const FILTER_TYPE = {
  CURRENT_PAGE: 'current_page',
  FIRST: 'first',
  PRIORITY: 'priority',
  STATUS: 'status',
  PAGE_SIZE: 'page_size',
  SORT_FIELD: 'sort_field',
  SORT_ORDER: 'sort_order',
  USER_NAME: 'user_name',
  CREATED_AT: 'created_at',
};

export const DEFAULT_FILTERS: SelectedFilters = {
  status: '',
  created_at: '',
  priority: '',
  user_name: '',
  page_size: 0,
  sort_field: '',
  sort_order: '',
  current_page: 0,
  first: 0,
};

export const DEFAULT_CURRENT_FILTERS: FilterOptions = {
  created_at: ['', ''],
  status: [],
  priority: [],
  user_name: [],
};

export const PRIORITIES: { [key: string]: Priority } = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const INGESTION_BATCH_FILTER_CONFIG: DrawerItemConfig = {
  size: 'medium',
  type: 'default',
  subtype: 'default',
};

export const BATCHES_TABLE_COLUMNS: TableColumnSchema[] = [
  {
    original_name: 'status',
    type: 'status',
    display_name: 'Status',
    width: 5,
  },
  {
    original_name: 'name',
    type: 'text',
    display_name: 'Batch name',
    width: 14,
  },
  {
    original_name: 'description',
    type: 'text',
    display_name: 'Description',
    width: 21,
  },
  {
    original_name: 'user_name',
    type: 'badge',
    display_name: 'Started by',
    width: 13,
  },
  {
    original_name: 'created_at',
    type: 'date',
    display_name: 'Started on',
    width: 8,
  },
  {
    original_name: 'elapsed',
    type: 'text',
    display_name: 'Time Elapsed',
    width: 8,
  },
  {
    original_name: 'priority',
    type: 'priority',
    display_name: 'Priority',
    width: 5,
  },
  {
    original_name: 'actions',
    type: 'button',
    display_name: 'Actions',
    width: 4,
  },
];

export const PREVIEW_BATCHES_TABLE_COLUMNS: TableColumnSchema[] = [
  {
    original_name: 'status',
    type: 'status',
    display_name: 'Status',
    width: 5,
  },
  {
    original_name: 'name',
    type: 'text',
    display_name: 'Batch name',
    width: 18,
  },
];

export const RUNS_TABLE_COLUMNS: TableColumnSchema[] = [
  {
    original_name: 'name',
    type: 'text',
    display_name: 'All Runs',
    width: 25,
  },
  {
    original_name: 'status',
    type: 'status',
    display_name: 'Status',
    width: 15,
  },
  {
    original_name: 'created_at',
    type: 'date',
    display_name: 'Started on',
    width: 15,
  },
  {
    original_name: 'elapsed',
    type: 'text',
    display_name: 'Time Elapsed',
    width: 15,
  },
];

export const TASKS_TABLE_COLUMNS: TableColumnSchema[] = [
  {
    original_name: 'name',
    type: 'text',
    display_name: 'Name of Task',
    width: 16,
  },
  {
    original_name: 'status',
    type: 'status',
    display_name: 'Status',
    width: 8,
  },
  {
    original_name: 'created_at',
    type: 'date',
    display_name: 'Started on',
    width: 8,
  },
];

export const DAG_LOADER_NODES: NodeStructure[] = [
  {
    id: '1',
    label: '',
    status: 'LOADING',
  },
  {
    id: '2',
    label: '',
    status: 'LOADING',
  },
  {
    id: '3',
    label: '',
    status: 'LOADING',
  },
  {
    id: '4',
    label: '',
    status: 'LOADING',
  },
];

export const DAG_LOADER_EDGES: EdgeStructure[] = [
  {
    source: '1',
    target: '2',
    loading: true,
  },
  {
    source: '2',
    target: '3',
    loading: true,
  },
  {
    source: '3',
    target: '4',
    loading: true,
  },
];

export const DAG_TABS = {
  CO_SCIENTIST: 'co-scientist',
  DETAILS: 'details',
  LOGS: 'logs',
  FILES: 'files',
  PARAMETERS: 'parameters',
};
